import React, { useMemo } from 'react';
import clsx from 'clsx';
import { SVGIcon } from '../SVGIcon/SVGIcon';
import { GapPointMarkerProps, GapStatus } from './VehicleGapDetection.types';
import { IconName } from '../SVGIcon';

/**
 * Komponent pojedynczego markera punktu kontrolnego szczeliny
 *
 * Składa się z:
 * 1. Ikona statusu (check-circle, x, missing)
 * 2. Okrągły badge z literą pozycji (A-J)
 * 3. Efekt cienia/glow wokół markera
 *
 * Renderowany jako zwykły HTML (div) z position: absolute
 */
export const GapPointMarker: React.FC<GapPointMarkerProps> = ({
  position,
  status,
  x,
  y,
  isHighlighted,
  onHover,
  onClick,
}) => {
  const getStatusConfig = (
    status: GapStatus,
  ): { icon: IconName; colorClass: string; strokeColor: string; textColor: string; bgColor: string } => {
    switch (status) {
      case 'OK':
        return {
          icon: 'ok-white',
          colorClass: 'fill-green-700',
          strokeColor: '#15803d', // green-700
          textColor: '#ffffff',
          bgColor: '#15803d', // green-700
        };
      case 'NIE_OK':
        return {
          icon: 'x',
          colorClass: 'fill-red-800',
          strokeColor: '#dc2626', // red-600
          textColor: '#ffffff',
          bgColor: '#991b1b', // red-800
        };
      case 'BRAK':
        return {
          icon: 'missing',
          colorClass: '',
          strokeColor: '#9ca3af', // gray-400
          textColor: '#ffffff',
          bgColor: '#6b7280', // gray-500
        };
    }
  };

  const baseConfig = getStatusConfig(status);

  // Gdy marker jest podświetlony, kolory zmieniają się na niebieskie, ale ikona zostaje oryginalna
  const config = isHighlighted
    ? {
        ...baseConfig,
        colorClass: 'fill-blue-500',
        strokeColor: '#0064FF',
        bgColor: '#0064FF',
      }
    : baseConfig;

  // Oblicz gradient dla efektu shadow/glow
  const gradientStyle = useMemo(() => {
    return {
      background: `radial-gradient(circle at center, transparent 0%, transparent 20%, ${config.strokeColor} 100%)`,
    };
  }, [config.strokeColor]);

  return (
    <div
      className="absolute pointer-events-auto cursor-pointer"
      style={{
        left: `${x}%`,
        top: `${y}%`,
      }}
      onMouseEnter={() => onHover(position)}
      onMouseLeave={() => onHover(null)}
      onClick={() => onClick(position)}
    >
      {/* Efekt shadow/glow (tło) */}
      <div
        className="absolute rounded-full"
        style={{
          width: '50px',
          height: '50px',
          left: '-25px',
          top: '-25px',
          ...gradientStyle,
          opacity: 0.8,
          zIndex: 1,
        }}
      />

      {/* Ikona statusu */}
      <div
        className={clsx('flex absolute justify-center items-center', isHighlighted && 'gap-marker-highlighted')}
        style={{
          width: '35px',
          height: '35px',
          left: '-17.5px',
          top: '-17.5px',
          zIndex: 3,
        }}
      >
        <SVGIcon
          name={config.icon}
          className="w-[35px] h-[35px] pointer-events-none"
          aria-label={`Punkt ${position}: ${status}`}
        />
      </div>

      {/* Badge z literą pozycji */}
      <div
        className="flex absolute justify-center items-center rounded-full border-2 font-inter"
        style={{
          width: '26px',
          height: '26px',
          left: '-13px',
          top: '-34px',
          backgroundColor: '#111438',
          borderColor: config.strokeColor,
          opacity: 0.95,
          zIndex: 4,
        }}
      >
        <span
          className="text-[14px] font-bold select-none whitespace-nowrap"
          style={{ color: config.textColor }}
        >
          {position}
        </span>
      </div>

      {/* Accessibility */}
      <div className="sr-only">
        Punkt {position}: {status}
      </div>
    </div>
  );
};
